import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import FilterTabs from 'tio-reimagined/components/filter-tabs';
import SkillChip from 'tio-reimagined/components/skill-chip';
import ActionableCard from 'tio-reimagined/components/actionable-card';
import StatCard from 'tio-reimagined/components/stat-card';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhSparkle,
  PhTarget,
  PhTrendUp,
  PhCurrencyDollar,
  PhClock,
  PhBuildings,
  PhGraduationCap,
  PhCertificate,
  PhHeart,
  PhCheckCircle,
  PhArrowRight,
  PhFunnel,
  PhLightning,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import type {
  EmployeeCareerProfile,
  CareerPlan,
  Recommendation,
  CareerSummary,
} from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysRecommendationsSignature {
  Args: {
    model: {
      recommendations: Recommendation[];
      employee: EmployeeCareerProfile;
      careerPlan: CareerPlan;
      summary: CareerSummary;
    };
  };
}

const RECOMMENDATION_CONTEXT = {
  advocate: {
    goal: 'Nurse Manager',
    skillGaps: 'Leadership, Budget, QI',
    taEligibility: '$5,250/year',
    priorities: 'Leadership, Informatics',
  },
  ups: {
    goal: 'Hub Supervisor',
    skillGaps: 'Scheduling, Coaching, Process Improvement',
    taEligibility: '$5,250/year',
    priorities: 'Safety, Leadership',
  },
  starbucks: {
    goal: 'Store Manager',
    skillGaps: 'Labor Planning, Financials, Coaching',
    taEligibility: '$5,250/year',
    priorities: 'Operations, Customer Experience',
  },
};

export default class CareerPathwaysRecommendations extends Component<CareerPathwaysRecommendationsSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @tracked activeFilter = 'all';
  @tracked savedItems: string[] = [];

  get recommendationContext() {
    if (this.reimaginedData.isDemoMode) {
      const clientId = getCurrentDemoClientData().client.id as keyof typeof RECOMMENDATION_CONTEXT;
      return RECOMMENDATION_CONTEXT[clientId] ?? RECOMMENDATION_CONTEXT.advocate;
    }

    const { employee, careerPlan, summary } = this.args.model;
    const goal = careerPlan?.targetRoleTitle || employee?.targetRole?.title || 'Your next role';
    const skillGaps = summary
      ? `${summary.totalGaps} gaps (${summary.criticalGaps} critical)`
      : 'Skill gaps pending';
    const taEligibility = employee?.taBenefit?.annualMax
      ? `${formatCurrency(employee.taBenefit.annualMax)}/year`
      : 'TA eligibility pending';
    const priorities = summary
      ? `${summary.skillsInProgress} in-progress skills`
      : 'Priorities pending';

    return { goal, skillGaps, taEligibility, priorities };
  }

  get recommendations() {
    return this.args.model.recommendations;
  }

  get filters() {
    const all = this.recommendations.length;
    const taEligible = this.recommendations.filter((rec) => rec.taEligible).length;
    const certifications = this.recommendations.filter(
      (rec) => rec.type === 'Certification'
    ).length;
    const courses = this.recommendations.filter((rec) => rec.type === 'Course').length;
    const free = this.recommendations.filter((rec) =>
      rec.cost.toLowerCase().includes('free')
    ).length;

    return [
      { id: 'all', label: 'All', count: all },
      { id: 'ta-eligible', label: 'TA Eligible', count: taEligible },
      { id: 'certifications', label: 'Certifications', count: certifications },
      { id: 'courses', label: 'Courses', count: courses },
      { id: 'free', label: 'Free/Low Cost', count: free },
    ];
  }

  setFilter = (filterId: string) => {
    this.activeFilter = filterId;
  };

  toggleSaved = (itemId: string) => {
    if (this.savedItems.includes(itemId)) {
      this.savedItems = this.savedItems.filter((id) => id !== itemId);
    } else {
      this.savedItems = [...this.savedItems, itemId];
    }
  };

  isSaved = (itemId: string) => {
    return this.savedItems.includes(itemId);
  };

  get filteredRecommendations() {
    if (this.activeFilter === 'all') return this.recommendations;
    if (this.activeFilter === 'ta-eligible')
      return this.recommendations.filter((r) => r.taEligible);
    if (this.activeFilter === 'certifications')
      return this.recommendations.filter((r) => r.type === 'Certification');
    if (this.activeFilter === 'courses')
      return this.recommendations.filter((r) => r.type === 'Course');
    if (this.activeFilter === 'free')
      return this.recommendations.filter((r) => r.cost.toLowerCase().includes('free'));
    return this.recommendations;
  }

  getBadgeIntent = (badge?: string) => {
    const intents: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
      'Best Match': 'success',
      'Company Priority': 'warning',
      Premium: 'primary',
      'Quick Win': 'default',
      'In Demand': 'primary',
    };
    if (!badge) return 'default';
    return intents[badge] || 'default';
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Header }}
      <PageHero @gradient="brand">
        <:default>
          <div class="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div class="min-w-0 flex-1">
              <div class="tio-row mb-2">
                <PhSparkle @size={{32}} class="shrink-0" />
                <Heading @level={{1}} class="text-white text-2xl">Smart Recommendations</Heading>
              </div>
              <Text class="text-violet-100 text-lg">
                AI-powered learning recommendations personalized for your career goals, current
                skills, and tuition assistance benefits.
              </Text>
            </div>
            <div
              class="bg-white/10 backdrop-blur rounded-lg p-4 text-center min-w-[120px] shrink-0"
            >
              <PhLightning @size={{32}} class="mx-auto mb-2" />
              <div class="font-bold">{{this.recommendations.length}} Matches</div>
              <Text @size="sm" class="text-violet-200">For You</Text>
            </div>
          </div>
        </:default>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 min-w-0">
        {{! Filters Sidebar }}
        <div class="space-y-4">
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhFunnel @size={{18}} />
                <span>Filter</span>
              </div>
            </:header>
            <:body>
              <FilterTabs
                @tabs={{this.filters}}
                @activeId={{this.activeFilter}}
                @onSelect={{this.setFilter}}
                @activeClass="tio-bg-brand-soft tio-fg-brand"
                @inactiveClass="hover:tio-bg-sunken tio-fg-secondary"
                @countActiveClass="bg-violet-200 tio-fg-brand"
                @countInactiveClass="bg-gray-200 tio-fg-secondary"
                class="tio-col-tight"
              />
            </:body>
          </Section>

          {{! Why These Recommendations }}
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhTarget @size={{18}} class="tio-fg-brand" />
                <span>Based On</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-3 text-sm">
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Your goal: <strong>{{this.recommendationContext.goal}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Your skill gaps:
                    <strong>{{this.recommendationContext.skillGaps}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>TA eligibility:
                    <strong>{{this.recommendationContext.taEligibility}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Company priorities:
                    <strong>{{this.recommendationContext.priorities}}</strong></span>
                </div>
              </div>
            </:body>
          </Section>

          {{! Saved Items }}
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhHeart @size={{18}} class="text-pink-500" />
                <span>Saved ({{this.savedItems.length}})</span>
              </div>
            </:header>
            <:body>
              {{#if this.savedItems.length}}
                <Text @variant="muted" @size="sm">
                  You've saved
                  {{this.savedItems.length}}
                  programs to review later.
                </Text>
                <Button @intent="primary" @appearance="outlined" class="w-full mt-3">
                  View Saved
                </Button>
              {{else}}
                <Text @variant="muted" @size="sm">
                  Click the heart icon to save programs you're interested in.
                </Text>
              {{/if}}
            </:body>
          </Section>
        </div>

        {{! Main Content }}
        <div class="lg:col-span-3 space-y-4 min-w-0">
          {{#each this.filteredRecommendations as |rec|}}
            <ActionableCard
              @title={{rec.title}}
              @matchScore={{rec.matchScore}}
              @showBanner={{rec.isTopPick}}
              @bannerText="Top Pick for You"
              @bannerClass="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-medium px-4 py-2 tio-row-tight"
              @cardClass={{if
                rec.isTopPick
                "bg-white rounded-lg border border-violet-300 ring-2 ring-violet-100 shadow-sm overflow-hidden"
                "bg-white rounded-lg border tio-border-default shadow-sm overflow-hidden"
              }}
            >
              <:badge>
                {{#if rec.badge}}
                  <Chip @intent={{this.getBadgeIntent rec.badge}}>
                    {{rec.badge}}
                  </Chip>
                {{/if}}
              </:badge>
              <:metadata>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tio-fg-muted">
                  <span class="tio-row-extra-tight">
                    <PhGraduationCap @size={{16}} class="shrink-0" />
                    <span class="truncate">{{rec.provider}}</span>
                  </span>
                  <span class="tio-row-extra-tight">
                    <PhCertificate @size={{16}} class="shrink-0" />
                    {{rec.type}}
                  </span>
                  <span class="tio-row-extra-tight">
                    <PhClock @size={{16}} class="shrink-0" />
                    {{rec.duration}}
                  </span>
                </div>
              </:metadata>

              <:skills>
                {{#each rec.skillsGained as |skill|}}
                  <SkillChip @name={{skill}} @intent="primary" @showLevel={{false}} />
                {{/each}}
              </:skills>

              <:eligibility>
                <div class="space-y-3">
                  <div class="tio-bg-brand-wash rounded-lg p-2">
                    <div class="tio-row-top-tight">
                      <PhSparkle @size={{18}} class="tio-fg-brand-accent mt-0.5" />
                      <Text @size="sm">
                        <strong>Why this?</strong>
                        {{rec.reason}}
                      </Text>
                    </div>
                  </div>

                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                      @icon={{PhCurrencyDollar}}
                      @value={{rec.cost}}
                      @label="Cost"
                      @color="green"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhTrendUp}}
                      @value={{rec.salaryImpact}}
                      @label="Avg Salary Impact"
                      @color="green"
                      @valueClass="tio-fg-success dark:text-green-400"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhCheckCircle}}
                      @value={{concat rec.completionRate "%"}}
                      @label="Completion Rate"
                      @color="blue"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhBuildings}}
                      @value={{if rec.taEligible "Yes" "No"}}
                      @label="TA Eligible"
                      @color={{if rec.taEligible "green" "gray"}}
                      @valueClass={{if
                        rec.taEligible
                        "tio-fg-success dark:text-green-400"
                        "tio-fg-muted"
                      }}
                      class="p-3"
                    />
                  </div>
                </div>
              </:eligibility>

              <:actions>
                <div class="flex gap-2">
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label={{if rec.taEligible "Apply with TA" "Learn More"}}
                  />
                  <Button @intent="default" @appearance="outlined" @label="Add to Plan" />
                </div>
                <button
                  type="button"
                  {{on "click" (fn this.toggleSaved rec.id)}}
                  class="p-2 rounded-full hover:bg-pink-50 transition-colors"
                >
                  <PhHeart
                    @size={{24}}
                    class="{{if
                        (this.isSaved rec.id)
                        'text-pink-500 fill-current'
                        'tio-fg-disabled'
                      }}"
                    @weight={{if (this.isSaved rec.id) "fill" "regular"}}
                  />
                </button>
              </:actions>
            </ActionableCard>
          {{/each}}

          {{! Load More }}
          <div class="text-center py-6">
            <Button @intent="default" @appearance="outlined">
              Load More Recommendations
            </Button>
            <Text @variant="muted" @size="sm" class="mt-2">
              Showing 5 of 23 recommendations
            </Text>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
